import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCustomerView } from '@/hooks/useCustomerView';
import { Handshake, IndianRupee } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPriceRequestsButton = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const pollIntervalRef = useRef(null);
  const audioRef = useRef(null);
  const prevUnreadRef = useRef(0);

  const { isCustomerView } = useCustomerView();
  const isAdmin = (user?.is_admin || user?.is_staff_user) && !isCustomerView;

  const fetchNotifications = useCallback(async () => {
    if (!token || !isAdmin) return;
    
    try {
      const response = await axios.get(`${API}/price-requests/admin/notifications?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const notifs = response.data.notifications || [];
      
      // Count unread
      const unread = notifs.filter(n => !n.read).length;
      
      // Play sound if new unread notifications
      if (unread > prevUnreadRef.current && prevUnreadRef.current >= 0) {
        playNotificationSound();
        // Show toast for new price request
        const newest = notifs.find(n => !n.read);
        if (newest && newest.type === 'price_request') {
          toast.info(newest.message, {
            icon: '💰',
            duration: 5000,
          });
        }
      }
      
      prevUnreadRef.current = unread;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Error fetching admin price notifications:', error);
    }
  }, [token, isAdmin]);

  const playNotificationSound = () => {
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleU0gXK7i5KtiKAk2kd/ypG0sCSaN4vWtdiURH4Hi97eBRQAJet/3voxWCAFw3fe9kF0HB2bc97uPXAcJZNz3u45dBwpl2/e7jl0IC2Ta97qOXggMY9n3uo5eCAxj2fe6jl4IDGPa97qNXwgNYtn3uY1gCA1i2Pe5jWAIDWLY97mNYAgOYdf3uYxhCQ5g1/e5jGEJD1/W97iLYgkPX9b3uItiCRBe1fe4i2MJEF3V97eLYwkRXNT3t4pkChFc0/e3imUKEVvS97aJZgoSWtL3topnChJa0fe2iWgLElnQ97WJaAsTV8/3tYhpCxNXz/e1iGoLFFbO97SHawsUVc33tIdsCxVUzPeziG0MFVPMd7KHbgwWUst3soZvDBZRyneyhm8NF1DJd7GFcA0XT8h3sIVxDRdOx3ewhHINGE3Gd6+DcwwYTMV3r4J0DBhMxXevgnUMGUvEd66BdgwZSsN3roB3DBlJwnevf3gMGkjCd658eQwaSMF3rXx6DBpHwHetfHsMG0a+d6x6fAwbRr13rHl9DBxFvHeseX4MHEW8d6t5fwwcRLt3q3iADB1Du3ereoEMHUK6d6p5ggwdQbl3qnmDDB5AuHepeYQMHj+3d6l4hQwfPrZ3qHeFDB8+tneod4YMHz21d6d3hwwgPLR3p3aIDB88s3emdokNIDyyd6Z1ig0hO7J3pnSLDSE7sXeldYwNITqwd6V0jQ0iOq93pHSODSI5rnemVpYMIjiqdqZalwwjN6h2plqYDSM1pXamYZoNJDOidqZlnA0lMJ52pmidDSYum3ampZ8OJiqXd6atnxAnJ5N4prKhECkkkXintaMRKyCNea67phItHIl6rr+pEy8YhHqvxqsUMROAe7HNrRYxDnt8ss6xFTQIgny00rQWNgF2fbbZtxc29299u+C7Gjvsb328+t0=');
        audioRef.current.volume = 0.3;
      }
      audioRef.current.play().catch(() => {});
    } catch (e) {
      // Ignore audio errors
    }
  };

  // Mark all as read and dismiss when visiting price requests page
  const markAllReadAndDismiss = useCallback(async () => {
    if (!token || !isAdmin) return;
    
    try {
      // First mark all as read
      await axios.post(`${API}/price-requests/admin/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Then dismiss all read notifications
      await axios.delete(`${API}/price-requests/admin/notifications/dismiss-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUnreadCount(0);
      prevUnreadRef.current = 0;
    } catch (error) {
      console.error('Error dismissing notifications:', error);
    }
  }, [token, isAdmin]);

  useEffect(() => {
    fetchNotifications();
    
    // Poll every 15 seconds
    pollIntervalRef.current = setInterval(fetchNotifications, 30000);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetchNotifications]);

  // Auto-dismiss notifications when on price-requests page
  useEffect(() => {
    if (location.pathname === '/admin/price-requests' && unreadCount > 0) {
      markAllReadAndDismiss();
    }
  }, [location.pathname, unreadCount, markAllReadAndDismiss]);

  const handleClick = () => {
    // Navigate directly to price requests page
    navigate('/admin/price-requests');
    
    // Mark as read and dismiss
    if (unreadCount > 0) {
      markAllReadAndDismiss();
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      className="relative h-8 w-8 p-0 hover:bg-amber-50"
      onClick={handleClick}
      data-testid="admin-price-requests-btn"
    >
      <Handshake className="w-4 h-4 text-amber-600" />
      {unreadCount > 0 && (
        <Badge 
          className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center bg-red-500 text-white text-xs animate-pulse"
        >
          {unreadCount}
        </Badge>
      )}
    </Button>
  );
};

export default AdminPriceRequestsButton;
